import React, { createContext, useContext, useState, useEffect } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState(() => {
    const stored = localStorage.getItem("wishlist");
    return stored ? JSON.parse(stored) : {};
  });

  // Safe number parsing helper
  const safeParseNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Lightweight function to fetch essential product details for wishlist validation
  const fetchProductDetails = async (productId) => {
    try {

      const res = await fetch(`${BASE_URL}/product/${productId}`);

      // Handle different response statuses
      if (!res.ok) {
        if (res.status === 404) {
          return { error: "Product not found", notFound: true };
        } else if (res.status === 204) {
          return { error: "Product not found", notFound: true };
        } else {
          return {
            error: `Failed to fetch product: ${res.status} ${res.statusText}`,
          };
        }
      }

      const data = await res.json();

      // Check for API response indicating product not found
      if (data?.status === "204" && data?.message === "Product not found") {
        return { error: "Product not found", notFound: true };
      }

      const p =
        data?.data && !Array.isArray(data.data)
          ? data.data
          : Array.isArray(data?.data)
            ? data.data[0]
            : data?.product;

      if (!p) {
        return { error: "Product not found", notFound: true };
      }


      // Parse main image
      let images = [];
      try {
        images = JSON.parse(p.product_images || "[]");
      } catch {}

      const mainImage =
        images.length > 0 ? `${CDN_URL}/${images[0]}` : "/placeholder.png";

      // Return only essential product details needed for wishlist
      const essentialDetails = {
        id: String(p.id),
        name: p.product_name ?? "",
        // Fix price mappings: max_retail_price is original price, your_price is offer price
        price: safeParseNumber(p.max_retail_price, 0), // Original price
        offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
        categoryId: String(p.category_id || ""),
        categoryName: p.category_name ?? "",
        subcategoryId: String(p.subcategory_id || ""),
        subcategoryName: p.subcategory_name ?? "",
        image: mainImage,
        // Add minimum order quantity field
        min_order_qty: safeParseNumber(p.min_order_qty, 1),
        minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
        // Keep any additional fields that might be needed for compatibility
        description: p.description ?? "",
        brand: p.brand_name,
        model: p.model_name,
        material: p.material,
        color: p.color,
        colorCode: p.color_code,
        size: p.size,
        quantity: parseInt(p.quantity) || 0,
        sku: p.sku,
        averageRating: p.average_rating,
        returnPolicy: p.product_policy,
        status: p.status,
      };

      return { product: essentialDetails };
    } catch (error) {
      return { error: "Network error or API unavailable" };
    }
  };

  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  const toggleWishlistItem = (userId, product) => {
    if (!userId) return;

    setWishlist((prev) => {
      const userWishlist = prev[userId] || [];
      const exists = userWishlist.some((item) => item.id === product.id);

      const updatedUserWishlist = exists
        ? userWishlist.filter((item) => item.id !== product.id)
        : [...userWishlist, product];

      return { ...prev, [userId]: updatedUserWishlist };
    });
  };

  const isInWishlist = (userId, productId) => {
    const userWishlist = wishlist[userId] || [];
    return userWishlist.some((item) => item.id === productId);
  };

  const getUserWishlist = (userId) => wishlist[userId] || [];

  // Remove specific product from user's wishlist
  const removeFromWishlist = (userId, productId) => {
    if (!userId) return;

    setWishlist((prev) => {
      const userWishlist = prev[userId] || [];
      const updatedUserWishlist = userWishlist.filter(
        (item) => item.id !== productId,
      );
      return { ...prev, [userId]: updatedUserWishlist };
    });
  };

  // Validate a single product using lightweight API call
  const validateProduct = async (product) => {
    try {

      const result = await fetchProductDetails(product.id);

      if (result.error) {

        return {
          isValid: false,
          product,
          reason: result.notFound ? "Product no longer exists" : result.error,
        };
      }

      if (result.product) {

        return {
          isValid: true,
          product: result.product,
          originalProduct: product,
        };
      } else {
        return {
          isValid: false,
          product,
          reason: "Product data not available",
        };
      }
    } catch (error) {

      // For any errors, assume product exists but temporarily unavailable
      // Return original product data
      return { isValid: true, product };
    }
  };

  // Validate all products in user's wishlist and update with fresh data
  const validateUserWishlist = async (userId) => {
    const userWishlist = getUserWishlist(userId);

    if (userWishlist.length === 0) {
      return { validProducts: [], invalidProducts: [] };
    }


    const validationPromises = userWishlist.map((product) =>
      validateProduct(product),
    );

    try {
      const results = await Promise.all(validationPromises);

      const validProducts = results
        .filter((result) => result.isValid)
        .map((result) => result.product);

      const invalidProducts = results
        .filter((result) => !result.isValid)
        .map((result) => ({ ...result.product, reason: result.reason }));


      if (invalidProducts.length > 0) {
      }

      if (validProducts.length > 0) {
      }

      return { validProducts, invalidProducts };
    } catch (error) {
      return { validProducts: userWishlist, invalidProducts: [] };
    }
  };

  // Update wishlist with fresh product data (for valid products)
  const updateWishlistWithFreshData = (userId, validProducts) => {
    if (!userId || validProducts.length === 0) return;

    setWishlist((prev) => {
      const updatedWishlist = { ...prev };
      updatedWishlist[userId] = validProducts;
      return updatedWishlist;
    });
  };

  // Remove invalid products from wishlist (silent operation - toasts handled by component)
  const removeInvalidProducts = (userId, invalidProducts) => {
    if (!userId || invalidProducts.length === 0) return;

    setWishlist((prev) => {
      const userWishlist = prev[userId] || [];
      const invalidProductIds = invalidProducts.map((product) => product.id);
      const updatedUserWishlist = userWishlist.filter(
        (item) => !invalidProductIds.includes(item.id),
      );

      return { ...prev, [userId]: updatedUserWishlist };
    });

    // No toast notifications here - handled by calling component to prevent duplicates
  };

  // Validate and clean user wishlist with fresh data (main function to call)
  const validateAndCleanWishlist = async (userId) => {
    if (!userId) return;

    try {
      const { validProducts, invalidProducts } =
        await validateUserWishlist(userId);

      // Update wishlist with fresh data for valid products
      if (validProducts.length > 0) {
        updateWishlistWithFreshData(userId, validProducts);
      }

      // Remove invalid products
      if (invalidProducts.length > 0) {
        removeInvalidProducts(userId, invalidProducts);
      }

      return { validProducts, invalidProducts };
    } catch (error) {
      return { validProducts: getUserWishlist(userId), invalidProducts: [] };
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        toggleWishlistItem,
        isInWishlist,
        getUserWishlist,
        removeFromWishlist,
        validateProduct,
        validateUserWishlist,
        removeInvalidProducts,
        validateAndCleanWishlist,
        updateWishlistWithFreshData,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
